import { AuthProvider, fetchUtils, QueryFunctionContext } from "react-admin";
import authStore, { AuthData } from './authStore';
import { apiRoutes } from '../apiRoutes';
import { mapValues, chain } from "lodash";



let refreshPromise: Promise<AuthData | null> | null = null;

const getToken = async (forceFetch = false): Promise<string> => {
    const refreshAuthData = async (): Promise<AuthData | null> => {
        const refreshEndpoint = `${apiRoutes.auth}/refresh-token`;
        const request = new Request(refreshEndpoint, {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json' }),
            credentials: 'include',
        });

        const response = await fetch(request);
        if (response.status === 200) {
            const { permissions, ...data } = await response.json();
            return {
                permissions: normalizePermissions(permissions),
                ...data
            };
        }
        return null;
    }

    const token = authStore.getToken();

    if (forceFetch || !token) {
        if (refreshPromise) {
            await refreshPromise;
        } else {
            try {
                refreshPromise = refreshAuthData();
                const data = await refreshPromise;

                if (data) {
                    authStore.storeAuthData(data);
                } else {
                    throw new Error('Unable to refresh token');
                }
            } finally {
                refreshPromise = null;
            }
        }
    }

    return authStore.getToken()!;
}

const ensureAuth = () => getToken();



export const fetchJsonInternal = async (url: string, options: fetchUtils.Options = {}) => {
    const token = await getToken();

    if (token) {
        options.user = {
            authenticated: true,
            token: `Bearer ${token}`
        };
    }
    try {
        return await fetchUtils.fetchJson(url, options);
    } catch (err) {
        console.error((err as Error).message)
        //1. refresh token
        try {

            const newToken = await getToken(true);
            //2.repeat last fetch operation
            options.user = {
                authenticated: true,
                token: `Bearer ${newToken}`
            };
            return await fetchUtils.fetchJson(url, options);
        } catch (err2) {
            console.log(err2)
            throw err2;
        }
    }

}

const normalizePermissions = (permissions: Record<string, { action: string[] }>): Record<string, Set<string>> => {
    return chain(permissions)
        .mapValues(({ action }) => new Set(action))
        .mapKeys((_, key) => key.toLowerCase())
        .value();
    // return mapValues(permissions, ({ action }) => new Set(action));
}
export const createAuthProvider = (): AuthProvider => {


    return ({
        // supportAbortSignal: true,
        login: async ({ username, password }) => {
            const request = new Request(`${apiRoutes.auth}/authenticate`, {
                method: 'POST',
                body: JSON.stringify({ username, password }),
                headers: new Headers({ 'Content-Type': 'application/json' }),
                credentials: 'include',
            });

            const response = await fetch(request);
            if (response.ok) {
                const { id, email, permissions, token, tokenExpiry } = await response.json();
                authStore.storeAuthData({
                    id,
                    email,
                    permissions: normalizePermissions(permissions),
                    token,
                    tokenExpiry
                });


            } else {
                throw new Error(response.statusText);
            }

        },

        logout: async () => {
            const request = new Request(`${apiRoutes.auth}/logout`, {
                method: 'POST',
                headers: new Headers({ 'Content-Type': 'application/json' }),
                credentials: 'include',
            });
            authStore.storeAuthData(undefined)

            await fetch(request);//.then(() => '/login');
        },

        checkAuth: async (params: QueryFunctionContext) => {

            try {
                const token = await getToken();
                if (!token) {
                    throw new Error("login.required");
                }
            } catch (err) {
                console.error(err)
                throw new Error("login.required");
            }

        },

        checkError: async (error) => {
            const status = error.status;
            if (status === 401
                // || status === 403
            ) {
                authStore.storeAuthData(undefined)
                throw new Error()
            }
            
        },


        getIdentity: async (params?: QueryFunctionContext) => {
            await ensureAuth();
            const identity = authStore.getUserData()!;
            if (!identity) {
                throw new Error("login.required");
            }
            const { id, email } = identity;
            return { id, email, fullName: email }

        },

        canAccess: async (param: { action: string, resource: string, record?: unknown }) => {
            const resource = param.resource.toLocaleLowerCase();
            const action = param.action;
            await ensureAuth();
            const { permissions } = authStore.getUserData() || {};

            const permissionsSet = permissions?.[resource] || DefaultSet;

            const access = permissionsSet && permissionsSet.has("*") || permissionsSet?.has(action) ? true : false;
            // console.log("canAccess?", { resource, action, access }, permissionsSet);
            return access;
        },
    });


}

const DefaultSet = new Set()

const _act: Record<string, string> = {
    create: "create",
    delete: "delete",
    list: "list",
    edit: "update",
    view: "view",

}

